import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { mockProviders, mockProviderCategories } from '../data/mock';
import {
  Search,
  MapPin,
  Star,
  CheckCircle2,
  Tag,
  Building2,
  ArrowRight,
  Filter,
  Shield,
  Award,
  ExternalLink
} from 'lucide-react';

const ProveedoresPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Todos');
  const [selectedLocation, setSelectedLocation] = useState('Todos');

  const locations = ['Todos', 'Nacional', 'Ciudad de México', 'Guadalajara', 'Monterrey'];

  const filteredProviders = mockProviders.filter(provider => {
    const matchesSearch = provider.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         provider.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'Todos' || provider.category === selectedCategory;
    const matchesLocation = selectedLocation === 'Todos' || provider.location === selectedLocation;
    return matchesSearch && matchesCategory && matchesLocation;
  });

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative py-20 lg:py-24 bg-gradient-to-br from-gray-50 via-white to-emerald-50 overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 right-20 w-96 h-96 bg-emerald-200 rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-20 w-80 h-80 bg-blue-200 rounded-full blur-3xl" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <Badge className="mb-4 bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
              <Shield className="h-3.5 w-3.5 mr-1.5" />
              Proveedores Certificados
            </Badge>
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Directorio de Proveedores UPIM
            </h1>
            <p className="text-xl text-gray-600">
              Encuentra proveedores verificados con descuentos exclusivos para afiliados UPIM.
            </p>
          </div>
        </div>
      </section>

      {/* Search and Filter */}
      <section className="py-6 bg-white border-b sticky top-16 lg:top-20 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Buscar proveedores..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex gap-3">
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-48">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Categoría" />
                </SelectTrigger>
                <SelectContent>
                  {mockProviderCategories.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={selectedLocation} onValueChange={setSelectedLocation}>
                <SelectTrigger className="w-48">
                  <MapPin className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Ubicación" />
                </SelectTrigger>
                <SelectContent>
                  {locations.map((loc) => (
                    <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </section>

      {/* Providers Grid */}
      <section className="py-16 lg:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <p className="text-gray-600">
              {filteredProviders.length} proveedor{filteredProviders.length !== 1 ? 'es' : ''} encontrado{filteredProviders.length !== 1 ? 's' : ''}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProviders.map((provider) => (
              <Card 
                key={provider.id} 
                className="overflow-hidden border-0 shadow-sm hover:shadow-lg transition-all duration-300 group"
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="h-14 w-14 rounded-xl bg-gray-100 overflow-hidden">
                        <img
                          src={provider.logo}
                          alt={provider.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 group-hover:text-emerald-600 transition-colors">
                          {provider.name}
                        </h3>
                        <Badge variant="outline" className="text-xs mt-1">
                          {provider.category}
                        </Badge>
                      </div>
                    </div>
                    {provider.verified && (
                      <div className="flex items-center">
                        <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                      </div>
                    )}
                  </div>
                  
                  <p className="text-gray-600 text-sm mb-4">{provider.description}</p>
                  
                  <div className="flex items-center text-sm text-gray-500 mb-3">
                    <MapPin className="h-4 w-4 mr-1" />
                    {provider.location}
                  </div>
                  
                  <div className="flex items-center mb-4">
                    <div className="flex items-center">
                      {[...Array(5)].map((_, i) => (
                        <Star 
                          key={i} 
                          className={`h-4 w-4 ${i < Math.floor(provider.rating) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} 
                        />
                      ))}
                      <span className="ml-2 text-sm text-gray-600">{provider.rating}</span>
                    </div>
                  </div>
                  
                  {/* Discount Badge */}
                  <div className="bg-emerald-50 rounded-lg p-3 mb-4">
                    <div className="flex items-center text-emerald-700">
                      <Tag className="h-4 w-4 mr-2" />
                      <span className="text-sm font-medium">{provider.discount}</span>
                    </div>
                  </div>
                  
                  {/* Services */}
                  <div className="flex flex-wrap gap-1 mb-4">
                    {provider.services.slice(0, 3).map((service, index) => (
                      <Badge key={index} variant="secondary" className="text-xs">
                        {service}
                      </Badge>
                    ))}
                    {provider.services.length > 3 && (
                      <Badge variant="secondary" className="text-xs">
                        +{provider.services.length - 3}
                      </Badge>
                    )}
                  </div>
                  
                  <Button 
                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                  >
                    Ver descuento
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          {filteredProviders.length === 0 && (
            <div className="text-center py-12 bg-white rounded-xl">
              <Building2 className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No se encontraron proveedores con esos criterios.</p>
            </div>
          )}
        </div>
      </section>

      {/* Become Provider */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4 bg-blue-100 text-blue-700 hover:bg-blue-100">
                <Award className="h-3.5 w-3.5 mr-1.5" />
                Para Proveedores
              </Badge>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                ¿Quieres ser proveedor certificado UPIM?
              </h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Conviértete en proveedor certificado y accede a más de 2,000 profesionales 
                inmobiliarios que buscan servicios de calidad.
              </p>
              
              <ul className="space-y-3 mb-8">
                <li className="flex items-center space-x-3">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                  <span className="text-gray-600">Visibilidad ante miles de profesionales inmobiliarios</span>
                </li>
                <li className="flex items-center space-x-3">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                  <span className="text-gray-600">Sello de certificación UPIM</span>
                </li>
                <li className="flex items-center space-x-3">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                  <span className="text-gray-600">Promoción en eventos y comunicaciones</span>
                </li>
                <li className="flex items-center space-x-3">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                  <span className="text-gray-600">Blindaje jurídico con contrato UPIM</span>
                </li>
              </ul>
              
              <Button 
                size="lg" 
                className="bg-emerald-600 hover:bg-emerald-700"
                asChild
              >
                <Link to="/contacto">
                  Solicitar certificación
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=600&h=450&fit=crop"
                alt="Proveedores UPIM"
                className="rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 lg:py-20 bg-emerald-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">
            Accede a descuentos exclusivos
          </h2>
          <p className="text-emerald-100 text-lg mb-8">
            Los afiliados UPIM tienen acceso a descuentos especiales en todos los 
            proveedores certificados del directorio.
          </p>
          <Button 
            size="lg" 
            className="bg-white text-emerald-600 hover:bg-emerald-50 px-8 h-12"
            asChild
          >
            <Link to="/afiliacion">
              Afíliate para ver descuentos
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </Button>
        </div>
      </section>
    </Layout>
  );
};

export default ProveedoresPage;
